import { Component, computed, DOCUMENT, Inject, inject, Renderer2, signal } from '@angular/core';
import { Navbar } from '../navbar/navbar';
import { Footer } from '../footer/footer';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Api } from '../../services/api';
import { catchError, delay, of, tap, timeout } from 'rxjs';
import { ProductBenefit, ProductFaq, ProductItem, ProductKeyword, ProductKeywordItem } from '../../interface/products';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { NgStyle } from '@angular/common';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { WhatOurCustomersSay } from '../what-our-customers-say/what-our-customers-say';
import { toSignal } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { Canonical } from '../../services/canonical';
import { TruncatePipe } from '../../pipes/truncate-pipe';

@Component({
  selector: 'app-product',
  imports: [
    Navbar,
    Footer,
    NgStyle,
    NgxSkeletonLoaderModule,
    LazyLoadImageModule,
    WhatOurCustomersSay,
    TruncatePipe,
    RouterLink
  ],
  templateUrl: './product.html',
  styleUrl: './product.css',
})
export class Product {
  isLoading = signal(false);
  hasBenefits = signal(false);
  title = inject(Title);
  meta = inject(Meta);
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  product = signal<ProductItem | undefined>(undefined);
  faqs = signal<ProductFaq[]>([]);
  benefits = signal<ProductBenefit[]>([]);
  keywords = signal<ProductKeywordItem[]>([]);
  defaultImage = 'assets/default-img.jpg';
  private api = inject(Api);
  private canonical = inject(Canonical);
  private http = inject(HttpClient);

  constructor(
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.activatedRoute.paramMap.subscribe(param => {
      const slug = param.get('slug')
      toSignal(this.http.get<ProductItem>(`${environment.apiUrl}/product/${slug}`));
      this.initialize(slug!);
    });
  }

  toBewellShop() : void {
    window.open('http://shop.bewell.com.ph', '_self');
  }

  private setMetaTags(obj: ProductItem) : void {
    this.title.setTitle(obj.setTitle);
    this.canonical.setCanonicalUrl(obj.canonical);
    this.meta.addTags(JSON.parse(obj.meta));
  }

  private setSchemaProduct(obj?: ProductItem) : void {
    if(obj?.schema!){
      const script = this.renderer.createElement('script');
      script.type = 'application/ld+json';
      script.text = obj?.schema?.product!;
      this.renderer.appendChild(this.document.head, script);
    }
  }

  private setSchemaFaqs(obj?: ProductItem) : void {
    if(obj?.schema?.faqs!){
      const script = this.renderer.createElement('script');
      script.type = 'application/ld+json';
      script.text = obj?.schema?.faqs!;
      this.renderer.appendChild(this.document.head, script);
    }
  }

  private initialize(obj: string) : void {
    this.isLoading.set(true);

    this.api.getProduct(obj)
    .pipe(
      timeout(10000),
      catchError(() => of(undefined)),
      tap(() => this.isLoading.set(false))
    ).subscribe(product => {
      if(!product) {
        this.router.navigate(['/page-not-found']);
      }else{
        this.hasBenefits.set(product?.benefits!.length! > 0);
        this.product.set(product!);
        this.faqs.set(product?.faqs!);
        this.benefits.set(product?.benefits!);
        this.keywords.set(product?.keywords?.items!);
        this.setMetaTags(product!);
        this.setSchemaProduct(product!);
        this.setSchemaFaqs(product!);
      }
    });
  }
}
